import "@/components/alerts/alerts.scss";

import { useContext, useEffect, useState } from "react";

import LinkIcon from "@mui/icons-material/Link";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Chip,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  Alert,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { getPublicBulletinDetail } from "@rubi/api-generated";
import { HighlightText, PageHeader } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useCrimeLabel } from "@/api/loadAlerts";
import { EmailButton, PhoneButton } from "@/common";
import CompareMediaDisplay from "@/common/CompareMediaDisplay";
import {
  formatDateLong,
  formatDateShort,
  formatDateWithoutTime,
} from "@/common/utils";
import AlertMediaSlider from "@/components/alerts/AlertMediaSlider";
import { LOCATION_CLASSIFICATION_REFERENCE } from "@/components/alerts/constants";
import {
  PublicAlertContact,
  PublicAlertDetailResource,
} from "@/components/alerts/public/types";
import {
  getAddressClassification,
  getAddressDescription,
} from "@/components/alerts/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { usePoliceServiceNameImage } from "@/hooks/useCommunities";
import { IEvidenceFile, ITag, MediaBlob } from "@/types/typesAlerts";

// Type guard function to verify if an object is an ITag
const isITag = (obj: unknown): obj is ITag => {
  return (
    !!obj &&
    typeof obj === "object" &&
    "id" in obj &&
    "title" in obj &&
    "description" in obj
  );
};

const fetchBlobAndCreateURL = async (url: string) => {
  const response = await fetch(url);
  const blob = await response.blob();
  const objectURL = URL.createObjectURL(blob);
  return { blob, objectURL };
};

const getMediaDimensions = (mediaType: "image" | "video", url: string) => {
  return new Promise((resolve, reject) => {
    if (mediaType === "image") {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = (err) => {
        reject(err);
      };
      img.src = url;
    } else if (mediaType === "video") {
      const video = document.createElement("video");
      video.onloadedmetadata = () => {
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
        });
      };
      video.onerror = (err) => {
        reject(err);
      };
      video.src = url;
    } else {
      reject(new Error("Unsupported media type"));
    }
  });
};

// 修改接口定义，简化props
interface InlinedDisplayAlertProps {
  groupId: string;
  alertProperties: PublicAlertDetailResource;
  view_token?: string | null;
}

const ModifiedInlinedDisplayAlert = ({
  groupId,
  alertProperties,
}: InlinedDisplayAlertProps) => {
  const policeServiceNameImage = usePoliceServiceNameImage();
  const { currentUserGroups } = useContext(AuthStateContext);
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation(["bulletin", "bulletin/classification"]);
  const locale = i18n.language;
  const crimeLabel = useCrimeLabel();

  // 直接从alertProperties中获取所需属性
  const {
    type,
    translated_type,
    operationalArea,
    incident_times: incidentTimes,
    bulletinId: alertId,
    tagDict = {},
    location: add,
    issuing_police_service_name: issuing_police_service = "",
    associated_crime: associatedCrimes = [],
    occurrence_number: occurrenceNumber,
    issued_time: issuedTime,
    tags = {},
    evidence_files: evidences = {},
    public_contact: public_contact = [],
  } = alertProperties || {};
  const addresses = add?.addresses ?? [];
  const incidentTimeItems = incidentTimes ?? [];
  const occurrenceNumbers = occurrenceNumber ?? [];
  // 确保tagDict是一个对象
  const safeTagDict: Record<string, ITag> = tagDict || {};

  const [lang, setLang] = useState("en");

  const [attachmentsArray, setAttachmentsArray] = useState<IEvidenceFile[]>(
    Object.values(evidences) as IEvidenceFile[],
  );
  const [attachmentBlobArray, setAttachmentBlobArray] = useState<MediaBlob[]>(
    [],
  );

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isSlideShowActive, setIsSlideShowActive] = useState(false);

  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme)?.breakpoints?.down("sm"));
  const [mediaLoading, setMediaLoading] = useState(false);

  const { inView, ref } = useInView({
    triggerOnce: true,
    delay: 250,
  });
  const navigate = useNavigate();
  const isSelectedAlert =
    searchParams.get("bulletinId") === alertId?.toString();
  const searchText = searchParams.get("searchText");

  useEffect(() => {
    if (selectedIndex === -1) {
      setIsSlideShowActive(false);
      return;
    }
    setIsSlideShowActive(true);
  }, [selectedIndex]);

  useEffect(() => {
    if (inView && Object.keys(evidences).length > 0) {
      const updatedAttachmentsArray = Object.values(
        evidences,
      ) as IEvidenceFile[];
      setAttachmentsArray(updatedAttachmentsArray);
      setMediaLoading(updatedAttachmentsArray.length > 0);

      const fetchBlobsAndVideos = async () => {
        const blobs = await Promise.all(
          updatedAttachmentsArray.map(async (attachment: IEvidenceFile) => {
            const url = attachment.content_type.includes("video")
              ? getDownloadUrl(attachment)
              : getThumbnailUrl(attachment);
            const { blob, objectURL } = await fetchBlobAndCreateURL(url);

            const mediaType = attachment.content_type.includes("video")
              ? "video"
              : "image";
            try {
              const dimensions = await getMediaDimensions(mediaType, objectURL);
              return {
                blob,
                filename: attachment?.filename || "",
                dimensions,
                media_type: mediaType,
              } as MediaBlob;
            } catch (err) {
              console.error("Error getting media dimensions", err);
              return {
                blob,
                filename: attachment?.filename || "",
                dimensions: { width: 0, height: 0 },
                media_type: mediaType,
              } as MediaBlob;
            } finally {
              URL.revokeObjectURL(objectURL);
            }
          }),
        );

        setMediaLoading(false);
        setAttachmentBlobArray(blobs);
      };

      fetchBlobsAndVideos();
    } else if (inView) {
      if (mediaLoading) {
        setMediaLoading(false);
      }
      setAttachmentBlobArray([]);
      setAttachmentsArray([]);
    }
  }, [evidences, inView]);

  const renderContactInformation = (contacts: PublicAlertContact[] | null) => {
    if (!contacts) return null;
    return contacts.map((contact) => {
      return (
        <>
          <div style={{ whiteSpace: "pre-wrap" }}>
            {contact?.name ?? contact.name}
            {contact?.email && (
              <EmailButton
                email={contact.email}
                subject={`Inquiry about ${translated_type ?? type} alert - ${occurrenceNumbers.join()}`}
              />
            )}
            {contact?.phone && (
              <PhoneButton
                phoneNumber={contact.phone}
                extension={contact.extension ?? undefined}
              />
            )}
          </div>
        </>
      );
    });
  };

  const getDownloadUrl = (attachment: IEvidenceFile) => {
    const url = new URL(attachment?.download_url ?? "");
    return url.toString();
  };

  const getMediaIndexUrl = (index) => {
    if (index === -1) return "";
    if (!attachmentsArray) return "";
    const item = attachmentsArray[index];

    return getDownloadUrl(item);
  };

  const getThumbnailUrl = (attachment: IEvidenceFile) => {
    const url = new URL(attachment?.thumbnail_url ?? attachment?.download_url);
    return url.toString();
  };

  let policeLogo;
  if (policeServiceNameImage[issuing_police_service])
    policeLogo = policeServiceNameImage[issuing_police_service];
  else policeLogo = policeServiceNameImage["DRDC"];

  const RubiconLogo = () => (
    <div className="logo">
      <b style={{ fontSize: 12, color: "grey" }}>POWERED BY</b>
      <img
        src="/Rubicon Logo.png"
        alt="Rubicon logo"
        style={{ height: "28px", marginTop: "-8px" }}
        onClick={() => window.open("https://tryRubicon.com", "_blank")}
      />
    </div>
  );

  const linkToMapView = (addressIndex: number) => {
    navigate(
      `/rubialert/public/group/${groupId}?view=map&occurrence_number=${occurrenceNumbers[0] ?? ""}&selectedAddressIndex=${addressIndex}`,
    );
  };

  const languageToggleForBulletin = () => {
    // Only show language toggle if bulletin is in French
    if (alertProperties.originalLanguage !== "fr") return null;
    return (
      <div className="mb-2 text-right fs-sm">
        {lang === "fr" ? (
          <div>
            View{" "}
            <a
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setLang("en")}
            >
              English
            </a>{" "}
            Bulletin
          </div>
        ) : (
          <div>
            This Bulletin is originally in {alertProperties.originalLanguage},
            View{" "}
            <a
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setLang("fr")}
            >
              French
            </a>{" "}
            Bulletin
          </div>
        )}
      </div>
    );
  };

  const incidentTimeText = (
    incidentTime: NonNullable<
      PublicAlertDetailResource["incident_times"]
    >[number],
  ) => {
    if (incidentTime.time_type === "range") {
      return `${formatDateShort(incidentTime.start_date, locale)} - ${formatDateShort(incidentTime.end_date, locale)}`;
    } else if (incidentTime.time_type === "unknown") {
      return `${t("bulletin:time_unknown")}`;
    } else if (incidentTime.time_type === "exact") {
      return `${formatDateLong(incidentTime.start_date, locale)}`;
    } else if (incidentTime.time_type === "not_applicable") {
      return `${t("bulletin:not_applicable")}`;
    } else if (incidentTime.time_type === "time_unknown") {
      return formatDateWithoutTime(incidentTime.start_date, locale);
    }
  };

  // Styles inlined from DisplayAlert.module.scss
  const styles = {
    alert_preview_container: "alert-preview-container mb-2",
    logo: "logo",
    header_container: "header-container",
    alert_heading_inner1: "alert-heading-inner1",
    police_logo: "police-logo",
    type_crimes_container: "type-crimes-container",
    crimes_container: "crimes-container",
    bulletin_container: "bulletin-container mx-2 mx-md-0 mx-auto px-3",
    tags_nodesc_container: "tags-nodesc-container",
    alertAddressLine: "alert-address-line",
    alertAddressPin: "alert-address-pin",
    selectedAlertAddressPin: "selected-alert-address-pin",
    compareAlertAddressPin: "compare-alert-address-pin",
    alertAddressHover: "alert-address-hover",
    attached_pdf_container: "attached-pdf-container",
    generated_pdf_container: "generated-pdf-container",
    publishedResolutions: "published-resolutions w-100",
  };

  return (
    <>
      <div
        className="mt-md-4 px-md-4 w-90 mx-auto"
        style={isSlideShowActive ? { height: "80vh", overflow: "hidden" } : {}}
      >
        <div className={styles.alert_preview_container} ref={ref}>
          <PageHeader
            onBack={() => {
              navigate("/rubialert/public/group/" + groupId);
            }}
            right={<RubiconLogo />}
          />

          <div className={styles.header_container}>
            <div className={styles.alert_heading_inner1}>
              <div>
                <img
                  src={policeLogo}
                  className={styles.police_logo}
                  alt="Police logo"
                />
                {operationalArea && (
                  <Typography variant="subtitle1" align="center">
                    {operationalArea}
                  </Typography>
                )}
              </div>

              <div className={styles.type_crimes_container}>
                <Typography
                  align="center"
                  variant={isSmall ? "h5" : "h4"}
                  style={{ textTransform: "uppercase" }}
                >
                  <b>{(translated_type ?? type) || <Skeleton />}</b>
                </Typography>

                <div className={styles.crimes_container}>
                  {associatedCrimes.map((crime, index) => (
                    <Chip
                      key={index}
                      variant="outlined"
                      label={crimeLabel(crime)}
                    />
                  ))}
                </div>
              </div>
              <div
                style={{ maxWidth: "80px", minWidth: isSmall ? "0px" : "60px" }}
              ></div>
            </div>
          </div>

          <div
            className="px-md-4 py-1 my-1 mx-auto"
            style={{
              maxWidth: "var(--page-max-width)",
            }}
          >
            <Alert
              severity="warning"
              icon={
                <WarningAmberIcon
                  fontSize="medium"
                  sx={{ alignSelf: "center" }}
                />
              }
              sx={{ mx: "auto", width: "95%", fontWeight: 600 }}
            >
              If you see them in public, do not take pictures, approach, or
              follow. For your safety, do not confront or attempt to apprehend
              anyone.
            </Alert>
            {/*
              No `entities`: the public resource does not carry them, so a public bulletin's photos
              are shown without a caption.
            */}
            <AlertMediaSlider
              medias={attachmentsArray}
              handleSlideClick={(index) => setSelectedIndex(index)}
            />
          </div>

          <div className={styles.bulletin_container}>
            {/* Language Toggle for bulletin only - if bulletin is in French, show toggle */}
            {languageToggleForBulletin()}
            {Object.keys(tags).length > 0 && (
              <>
                <Grid container spacing={2}>
                  {Object.values(tags)
                    .filter(isITag)
                    .map(
                      (tag) =>
                        tag.description !== "" && (
                          <Grid
                            size={{ xs: 12, md: 6 }}
                            key={tag.id.toString()}
                          >
                            <Tooltip title={tag.title}>
                              <Chip
                                label={
                                  <HighlightText
                                    text={tag.title}
                                    searchText={searchText}
                                  />
                                }
                                color="secondary"
                                style={{
                                  height: "24px",
                                  maxWidth: "160px",
                                }}
                              />
                            </Tooltip>
                            <Typography
                              variant="body1"
                              style={{ whiteSpace: "pre-line" }}
                              paragraph
                            >
                              <HighlightText
                                searchText={searchText}
                                text={
                                  lang === "fr"
                                    ? (tag.description_fr ?? tag.description)
                                    : tag.description
                                }
                              />
                            </Typography>
                          </Grid>
                        ),
                    )}
                </Grid>
                <div className={styles.tags_nodesc_container}>
                  {Object.values(safeTagDict)
                    .filter(isITag)
                    .map(
                      (tag) =>
                        tag.description === "" && (
                          <div key={tag.id.toString()}>
                            <Chip
                              label={
                                <HighlightText
                                  text={tag.title}
                                  searchText={searchText}
                                />
                              }
                              color="secondary"
                              title={tag.description}
                              style={{
                                height: "24px",
                                maxWidth: "160px",
                              }}
                            />
                            <HighlightText
                              searchText={searchText}
                              text={
                                lang === "fr"
                                  ? (tag.description_fr ?? tag.description)
                                  : tag.description
                              }
                            />
                          </div>
                        ),
                    )}
                </div>
              </>
            )}

            {occurrenceNumbers.length > 0 && (
              <Typography variant="subtitle1">
                Reference Number: <b>{occurrenceNumbers.join(", ")}</b>
              </Typography>
            )}

            <div style={{ display: "grid" }}>
              {incidentTimeItems.map((time, index) => (
                <Typography variant="body1" key={index}>
                  {t("bulletin:incident_time", { index: index + 1 })}{" "}
                  <b>{incidentTimeText(time)}</b>
                </Typography>
              ))}
            </div>

            <div style={{ display: "grid" }}>
              {addresses?.map((address, index) => {
                const classification =
                  getAddressClassification(address) ||
                  LOCATION_CLASSIFICATION_REFERENCE["Incident Address"];
                const isDuplicate =
                  addresses.filter(
                    (addr) => getAddressClassification(addr) === classification,
                  ).length > 1;
                const isMapView = searchParams.get("view") === "map";
                return (
                  <Typography
                    variant="body1"
                    key={index}
                    className={styles.alertAddressLine}
                  >
                    {isMapView ? (
                      <span
                        className={`${styles.alertAddressPin} ${isSelectedAlert ? styles.selectedAlertAddressPin : styles.compareAlertAddressPin}`}
                      >
                        {index + 1}
                      </span>
                    ) : (
                      ""
                    )}
                    {classification}
                    {isDuplicate ? ` ${index + 1}` : ""}:{" "}
                    {!alertProperties.isDraft ? (
                      <a
                        className={styles.alertAddressHover}
                        onClick={() => linkToMapView(index)}
                      >
                        <b>{getAddressDescription(address)}</b>
                        <LinkIcon
                          sx={{ marginLeft: "8px", verticalAlign: "middle" }}
                          fontSize="small"
                        />
                      </a>
                    ) : (
                      <b>{getAddressDescription(address)}</b>
                    )}
                  </Typography>
                );
              })}
            </div>
            {issuing_police_service && (
              <Typography variant="subtitle1">
                Agency:<b> {issuing_police_service}</b>
              </Typography>
            )}

            <br></br>

            <Typography
              variant="body1"
              style={{ whiteSpace: "pre-line" }}
              paragraph
            >
              <HighlightText
                searchText={searchText}
                text={
                  lang === "fr"
                    ? alertProperties.descriptionFr
                    : alertProperties.description
                }
              />
            </Typography>
            <Typography variant="h6">
              If you have any information, please contact:
            </Typography>

            <div style={{ whiteSpace: "pre-line" }}>
              {renderContactInformation(public_contact)}
            </div>

            {issuedTime ? (
              <Typography align="center" variant="caption">
                {t("bulletin:issued_on")} {formatDateLong(issuedTime, locale)}
              </Typography>
            ) : (
              <Typography align="center" variant="caption">
                {t("bulletin:issued_on")} <i>(Pending)</i>:{" "}
                {formatDateLong(new Date(), locale)}
              </Typography>
            )}

            <Typography
              variant="caption"
              display="block"
              align="center"
              sx={{ mt: 1, color: "text.secondary", width: "95%", mx: "auto" }}
            >
              Reminder that all persons identified are innocent until proven
              guilty in a court of law.
            </Typography>
          </div>
          {Object.keys(evidences).length > 0 && (
            <div className={styles.attached_pdf_container}>
              {/* Note: DisplayAttachedPDF component would need to be implemented */}
              {/* <DisplayAttachedPDF
                pdfFile={pdf}
                mode={mode}
                view_token={view_token}
              /> */}
            </div>
          )}
          {isSlideShowActive && (
            <CompareMediaDisplay
              isSlideShowActive={isSlideShowActive}
              setIsSlideShowActive={setIsSlideShowActive}
              mediaSrc={getMediaIndexUrl(selectedIndex)}
              mediaType={
                attachmentBlobArray[selectedIndex]?.media_type === "image"
                  ? "image"
                  : "video"
              }
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              slideshowLength={attachmentsArray.length}
            />
          )}
        </div>
      </div>
    </>
  );
};

interface IPublicAlertDetailProps {
  groupId?: string;
}

export const PublicAlertDetail = ({ groupId }: IPublicAlertDetailProps) => {
  const [searchParams] = useSearchParams();
  const encryptedId = searchParams.get("id") ?? "";
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [alertProperties, setAlertProperties] =
    useState<PublicAlertDetailResource | null>(null);
  const params = useParams();
  const urlGroupId = params.groupId;

  const getBulletinByEncryptedId = (id: string, group_id: number) => {
    setLoading(true);
    getPublicBulletinDetail({
      path: { group_id, bulletin_id: id },
    })
      .then((res) => {
        setAlertProperties(res.data as PublicAlertDetailResource);
        setError(null);
      })
      .catch((err) => {
        console.error("Error fetching bulletin:", err);
        setError("Bulletin not found or no longer available");
        toast.error("Bulletin not found or no longer available");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!encryptedId || encryptedId === "") {
      setError("No bulletin ID provided");
      setLoading(false);
      return;
    }

    // Use URL param groupId instead of the prop, and convert to number
    const groupIdToUse = urlGroupId
      ? parseInt(urlGroupId, 10)
      : groupId
        ? parseInt(groupId, 10)
        : undefined;

    if (!groupIdToUse) {
      setError("No group ID provided");
      setLoading(false);
      return;
    }

    getBulletinByEncryptedId(encryptedId, groupIdToUse);
  }, [encryptedId, urlGroupId, groupId]);

  if (loading) {
    return <div className="alert-container loading">Loading bulletin...</div>;
  }

  if (error) {
    return <div className="alert-container error">{error}</div>;
  }

  return (
    <div className="alert-container">
      {alertProperties && urlGroupId && (
        <ModifiedInlinedDisplayAlert
          groupId={urlGroupId}
          alertProperties={alertProperties}
        />
      )}
    </div>
  );
};
